import {
  MARKET_METHODOLOGY_VERSION,
  type MarketAnalysisComparable,
  type MarketPropertySnapshot,
  type MarketSubjectSnapshot,
  type SimilarityComponent,
  type SimilarityFactor,
  type SimilarityScoreBreakdown
} from "../domain/types.ts";
import { normalizeLocationText } from "../location/locationSearch.ts";
import { normalizeRoomCount } from "../data/roomNormalization.ts";
import { isMeaningfulMarketLocation } from "../domain/listingIdentity.ts";
import {
  canonicalCondition,
  canonicalPropertyType,
  effectiveApartmentArea,
  normalizeSlovakLocationName,
  sameMunicipality
} from "../domain/propertyMatching.ts";

const FACTOR_WEIGHTS: Record<SimilarityFactor, number> = {
  location: 30,
  area: 25,
  propertyType: 0,
  rooms: 0,
  technicalCondition: 22,
  recency: 8,
  floorAndElevator: 8,
  equipment: 7
};

const normalizeText = normalizeLocationText;

const isKnownText = (value?: string) => {
  const normalized = normalizeText(value ?? "");
  return Boolean(normalized && ![
    "unknown",
    "nezistene",
    "nezname",
    "neuvedene",
    "neurcene",
    "n a",
    "-"
  ].includes(normalized));
};

const textScore = (first: string, second: string) => {
  if (!isKnownText(first) || !isKnownText(second)) return undefined;
  const left = normalizeText(first);
  const right = normalizeText(second);
  if (!left || !right) return undefined;
  if (left === right) return 1;
  if (left.includes(right) || right.includes(left)) return 0.75;
  return 0;
};

const averageAvailable = (scores: Array<number | undefined>) => {
  const available = scores.filter((score): score is number => typeof score === "number");
  return available.length > 0 ? available.reduce((sum, score) => sum + score, 0) / available.length : undefined;
};

const sameKnownText = (first: string, second: string) => textScore(first, second) === 1;

const streetValue = (property: MarketPropertySnapshot) => property.location?.street || property.street;
const microlocationValue = (property: MarketPropertySnapshot) => property.location?.microlocation;
const localityValue = (property: MarketPropertySnapshot) => property.location?.locality || property.locality;
const sameLocationText = (first?: string, second?: string) => {
  const left = normalizeSlovakLocationName(first);
  const right = normalizeSlovakLocationName(second);
  return Boolean(left && right && left === right);
};

const granularLocation = (property: MarketPropertySnapshot) => [streetValue(property), microlocationValue(property), localityValue(property)]
  .find((value) => isMeaningfulMarketLocation(value, property.city));

const locationScore = (subject: MarketPropertySnapshot, comparable: MarketPropertySnapshot) => {
  if (!sameMunicipality(subject, comparable)) return 0;
  if (subject.streetId && comparable.streetId && subject.streetId === comparable.streetId) return 1;
  if (isMeaningfulMarketLocation(streetValue(subject), subject.city)
    && isMeaningfulMarketLocation(streetValue(comparable), comparable.city)
    && sameLocationText(streetValue(subject), streetValue(comparable))) return 1;
  if (isMeaningfulMarketLocation(microlocationValue(subject), subject.city)
    && isMeaningfulMarketLocation(microlocationValue(comparable), comparable.city)
    && sameLocationText(microlocationValue(subject), microlocationValue(comparable))) return 0.9;
  if (isMeaningfulMarketLocation(localityValue(subject), subject.city)
    && isMeaningfulMarketLocation(localityValue(comparable), comparable.city)
    && sameLocationText(localityValue(subject), localityValue(comparable))) return 0.8;
  if (granularLocation(subject) && granularLocation(comparable)) return 0.3;
  return 0.2;
};

const locationMatch = (subject: MarketPropertySnapshot, comparable: MarketPropertySnapshot) => {
  if (!sameMunicipality(subject, comparable)) return "different-municipality" as const;
  if (subject.streetId && comparable.streetId && subject.streetId === comparable.streetId) return "street" as const;
  if (isMeaningfulMarketLocation(streetValue(subject), subject.city)
    && isMeaningfulMarketLocation(streetValue(comparable), comparable.city)
    && sameLocationText(streetValue(subject), streetValue(comparable))) return "street" as const;
  if (isMeaningfulMarketLocation(microlocationValue(subject), subject.city)
    && isMeaningfulMarketLocation(microlocationValue(comparable), comparable.city)
    && sameLocationText(microlocationValue(subject), microlocationValue(comparable))) return "microlocation" as const;
  if (isMeaningfulMarketLocation(localityValue(subject), subject.city)
    && isMeaningfulMarketLocation(localityValue(comparable), comparable.city)
    && sameLocationText(localityValue(subject), localityValue(comparable))) return "locality" as const;
  if (granularLocation(subject) && granularLocation(comparable)) return "different-granular" as const;
  return "city-only" as const;
};

type ConfidenceAttribute = {
  label: string;
  weight: number;
  subjectKnown: boolean;
  comparableKnown: boolean;
};

const knownText = isKnownText;
const knownNumber = (value?: number) => typeof value === "number" && Number.isFinite(value);
const knownTriState = (value?: string) => Boolean(value && value !== "unknown");

export const calculateDataConfidence = (
  subject: MarketSubjectSnapshot,
  comparable: Pick<MarketAnalysisComparable, keyof MarketPropertySnapshot | "capturedAt">
) => {
  const attributes: ConfidenceAttribute[] = [
    { label: "mesto", weight: 5, subjectKnown: knownText(subject.city), comparableKnown: knownText(comparable.city) },
    { label: "typ nehnuteľnosti", weight: 4, subjectKnown: knownText(subject.propertyType), comparableKnown: knownText(comparable.propertyType) },
    { label: "počet izieb", weight: 4, subjectKnown: knownText(subject.rooms), comparableKnown: knownText(comparable.rooms) },
    { label: "ulica alebo mikrolokalita", weight: 15, subjectKnown: Boolean(granularLocation(subject)), comparableKnown: Boolean(granularLocation(comparable)) },
    { label: "čistá výmera bytu", weight: 15, subjectKnown: Boolean(effectiveApartmentArea(subject).value), comparableKnown: effectiveApartmentArea(comparable).confidence !== "low" },
    { label: "stav", weight: 10, subjectKnown: canonicalCondition(subject.technical?.condition ?? subject.condition) !== "unknown", comparableKnown: canonicalCondition(comparable.technical?.condition ?? comparable.condition) !== "unknown" },
    { label: "rozsah rekonštrukcie", weight: 8, subjectKnown: knownText(subject.reconstruction), comparableKnown: knownText(comparable.reconstruction) },
    { label: "balkón alebo lodžia", weight: 6, subjectKnown: knownText(subject.balconyType), comparableKnown: knownText(comparable.balconyType) },
    { label: "poschodie", weight: 7, subjectKnown: knownNumber(subject.floor), comparableKnown: knownNumber(comparable.floor) },
    { label: "výťah", weight: 7, subjectKnown: knownText(subject.elevator), comparableKnown: knownText(comparable.elevator) },
    { label: "stav domu", weight: 4, subjectKnown: knownText(subject.buildingCondition), comparableKnown: knownText(comparable.buildingCondition) },
    { label: "typ stavby", weight: 4, subjectKnown: knownText(subject.buildingType), comparableKnown: knownText(comparable.buildingType) },
    { label: "elektroinštalácia", weight: 4, subjectKnown: knownText(subject.electricity), comparableKnown: knownText(comparable.electricity) },
    { label: "bytové jadro", weight: 4, subjectKnown: knownText(subject.coreType), comparableKnown: knownText(comparable.coreType) },
    { label: "kuchyňa", weight: 4, subjectKnown: knownText(subject.kitchen), comparableKnown: knownText(comparable.kitchen) },
    { label: "pivnica", weight: 3, subjectKnown: knownText(subject.cellar), comparableKnown: knownText(comparable.cellar) },
    { label: "orientácia", weight: 3, subjectKnown: knownText(subject.orientation), comparableKnown: knownText(comparable.orientation) },
    { label: "dátum ponuky", weight: 3, subjectKnown: true, comparableKnown: Number.isFinite(Date.parse(comparable.capturedAt)) }
  ].filter((attribute) => attribute.subjectKnown);
  const totalWeight = attributes.reduce((sum, attribute) => sum + attribute.weight, 0);
  const known = attributes.filter((attribute) => attribute.comparableKnown);
  const knownWeight = known.reduce((sum, attribute) => sum + attribute.weight, 0);
  const score = totalWeight > 0 ? Math.round((knownWeight / totalWeight) * 100) : 0;
  const level = score >= 75 ? "high" as const : score >= 50 ? "medium" as const : "low" as const;
  const unknownAttributes = attributes.filter((attribute) => !attribute.comparableKnown).map((attribute) => attribute.label);
  const knownAttributes = known.map((attribute) => attribute.label);
  const reason = level === "high"
    ? "Kľúčové údaje sú dobre zdokumentované."
    : unknownAttributes.length > 0
      ? `Chýbajú: ${unknownAttributes.slice(0, 3).join(", ")}${unknownAttributes.length > 3 ? ` a ďalšie ${unknownAttributes.length - 3}` : ""}.`
      : "Rozsah údajov je obmedzený.";
  return { score, level, reason, knownAttributes, unknownAttributes };
};

const areaScore = (subject: MarketPropertySnapshot, comparable: MarketPropertySnapshot) => {
  const subjectArea = effectiveApartmentArea(subject).value;
  const comparableArea = effectiveApartmentArea(comparable).value;
  if (!subjectArea || !comparableArea) return undefined;
  const relativeDifference = Math.abs(subjectArea - comparableArea) / Math.max(subjectArea, comparableArea);
  // Linear decay: exact match = 1, 60 % relative difference = 0.
  return Math.max(0, 1 - relativeDifference / 0.6);
};

const roomNumber = (value: string) => normalizeRoomCount(value);

const roomsScore = (subjectRooms: string, comparableRooms: string) => {
  const subject = roomNumber(subjectRooms);
  const comparable = roomNumber(comparableRooms);
  if (subject === undefined || comparable === undefined) return textScore(subjectRooms, comparableRooms);
  return Math.max(0, 1 - Math.abs(subject - comparable) / 3);
};

const conditionRank = (value: string) => {
  const condition = canonicalCondition(value);
  return condition === "original" ? 0
    : condition === "shell" ? 0.5
      : condition === "partial_reconstruction" ? 1
        : condition === "full_reconstruction" ? 2
          : condition === "new_build" ? 3
            : undefined;
};

const conditionScore = (first: string, second: string) => {
  const firstRank = conditionRank(first);
  const secondRank = conditionRank(second);
  if (firstRank === undefined || secondRank === undefined) return textScore(first, second);
  return Math.max(0, 1 - Math.abs(firstRank - secondRank) / 3);
};

const technicalScore = (subject: MarketPropertySnapshot, comparable: MarketPropertySnapshot) =>
  averageAvailable([
    conditionScore(subject.technical?.condition ?? subject.condition, comparable.technical?.condition ?? comparable.condition),
    textScore(subject.reconstruction, comparable.reconstruction),
    textScore(subject.technical?.electrical ?? subject.electricity, comparable.technical?.electrical ?? comparable.electricity),
    textScore(subject.technical?.coreBathroom ?? subject.coreType, comparable.technical?.coreBathroom ?? comparable.coreType),
    textScore(subject.technical?.kitchen ?? subject.kitchen, comparable.technical?.kitchen ?? comparable.kitchen),
    textScore(subject.technical?.windows ?? "", comparable.technical?.windows ?? ""),
    textScore(subject.technical?.floors ?? "", comparable.technical?.floors ?? ""),
    textScore(subject.technical?.doors ?? "", comparable.technical?.doors ?? ""),
    textScore(subject.technical?.buildingCondition ?? subject.buildingCondition, comparable.technical?.buildingCondition ?? comparable.buildingCondition),
    textScore(subject.buildingType ?? "", comparable.buildingType ?? "")
  ]);

const recencyScore = (capturedAt: string, sourceCutoffAt: string) => {
  const captured = Date.parse(capturedAt);
  const cutoff = Date.parse(sourceCutoffAt);
  if (!Number.isFinite(captured) || !Number.isFinite(cutoff)) return undefined;
  const ageDays = Math.max(0, (cutoff - captured) / 86_400_000);
  if (ageDays <= 30) return 1;
  if (ageDays <= 90) return 0.85;
  if (ageDays <= 180) return 0.65;
  if (ageDays <= 365) return 0.4;
  return 0.2;
};

const floorScore = (subject?: number, comparable?: number) => {
  if (subject === undefined || comparable === undefined) return undefined;
  return Math.max(0, 1 - Math.abs(subject - comparable) / 8);
};

const floorAndElevatorScore = (subject: MarketPropertySnapshot, comparable: MarketPropertySnapshot) =>
  averageAvailable([
    floorScore(subject.floor, comparable.floor),
    textScore(subject.elevator, comparable.elevator),
    subject.floor !== undefined && subject.totalFloors !== undefined && comparable.floor !== undefined && comparable.totalFloors !== undefined
      ? Number(subject.floor === subject.totalFloors) === Number(comparable.floor === comparable.totalFloors) ? 1 : 0
      : undefined,
    subject.floor !== undefined && comparable.floor !== undefined
      ? Number(subject.floor === 0) === Number(comparable.floor === 0) ? 1 : 0
      : undefined
  ]);

const equipmentScore = (subject: MarketPropertySnapshot, comparable: MarketPropertySnapshot) =>
  averageAvailable([
    textScore(subject.balconyType, comparable.balconyType),
    textScore(subject.cellar, comparable.cellar),
    textScore(subject.orientation, comparable.orientation),
    knownTriState(subject.equipment?.balcony) && knownTriState(comparable.equipment?.balcony)
      ? Number(subject.equipment?.balcony === comparable.equipment?.balcony) : undefined,
    knownTriState(subject.equipment?.loggia) && knownTriState(comparable.equipment?.loggia)
      ? Number(subject.equipment?.loggia === comparable.equipment?.loggia) : undefined,
    knownTriState(subject.equipment?.terrace) && knownTriState(comparable.equipment?.terrace)
      ? Number(subject.equipment?.terrace === comparable.equipment?.terrace) : undefined,
    knownTriState(subject.equipment?.cellar) && knownTriState(comparable.equipment?.cellar)
      ? Number(subject.equipment?.cellar === comparable.equipment?.cellar) : undefined,
    knownTriState(subject.equipment?.parking) && knownTriState(comparable.equipment?.parking)
      ? Number(subject.equipment?.parking === comparable.equipment?.parking) : undefined,
    knownTriState(subject.equipment?.airConditioning) && knownTriState(comparable.equipment?.airConditioning)
      ? Number(subject.equipment?.airConditioning === comparable.equipment?.airConditioning) : undefined,
    textScore(subject.equipment?.orientation ?? "", comparable.equipment?.orientation ?? "")
  ]);

const component = (factor: SimilarityFactor, score: number | undefined): SimilarityComponent => ({
  weight: FACTOR_WEIGHTS[factor],
  available: score !== undefined,
  score: score === undefined ? undefined : Math.round(score * 1000) / 1000
});

const comparisonExplanations = (subject: MarketSubjectSnapshot, comparable: MarketPropertySnapshot, capturedAt: string, cutoffAt: string) => {
  const explanations: string[] = [];
  const location = locationMatch(subject, comparable);
  if (location === "street") explanations.push("✓ rovnaká ulica");
  else if (location === "microlocation") explanations.push("✓ rovnaká mikrolokalita");
  else if (location === "locality") explanations.push("✓ rovnaká lokalita");
  if (canonicalPropertyType(subject.propertyType) === canonicalPropertyType(comparable.propertyType)) explanations.push("✓ rovnaký typ nehnuteľnosti");
  if (roomsScore(subject.rooms, comparable.rooms) === 1) explanations.push("✓ rovnaký počet izieb");
  const subjectArea = effectiveApartmentArea(subject).value;
  const comparableArea = effectiveApartmentArea(comparable).value;
  if (subjectArea && comparableArea) {
    const difference = Math.round(comparableArea - subjectArea);
    explanations.push(`${Math.abs(difference) <= 2 ? "✓" : "△"} výmera ${difference >= 0 ? "+" : ""}${difference} m²`);
  }
  const subjectCondition = canonicalCondition(subject.technical?.condition ?? subject.condition);
  const comparableCondition = canonicalCondition(comparable.technical?.condition ?? comparable.condition);
  if (subjectCondition !== "unknown" && comparableCondition !== "unknown") {
    explanations.push(subjectCondition === comparableCondition ? "✓ rovnaký stav" : "△ odlišný stav");
  }
  if (textScore(subject.reconstruction, comparable.reconstruction) === 1) explanations.push("✓ rovnaký rozsah rekonštrukcie");
  if (textScore(subject.elevator, comparable.elevator) === 1) explanations.push("✓ rovnaká dostupnosť výťahu");
  if (textScore(subject.balconyType, comparable.balconyType) === 1) explanations.push("✓ rovnaký balkón / lodžia");
  if (textScore(subject.buildingCondition, comparable.buildingCondition) === 1) explanations.push("✓ rovnaký typ alebo stav domu");
  const recency = recencyScore(capturedAt, cutoffAt);
  if (recency !== undefined && recency >= 0.85) explanations.push("✓ aktuálna ponuka");
  return explanations.slice(0, 8);
};

export const calculateSimilarityScore = (
  subject: MarketSubjectSnapshot,
  comparable: Pick<MarketAnalysisComparable, keyof MarketPropertySnapshot | "capturedAt">,
  sourceCutoffAt: string
): SimilarityScoreBreakdown => {
  const components: SimilarityScoreBreakdown["components"] = {
    location: component("location", locationScore(subject, comparable)),
    area: component("area", areaScore(subject, comparable)),
    propertyType: component("propertyType", canonicalPropertyType(subject.propertyType) === canonicalPropertyType(comparable.propertyType) ? 1 : 0),
    rooms: component("rooms", roomsScore(subject.rooms, comparable.rooms)),
    technicalCondition: component("technicalCondition", technicalScore(subject, comparable)),
    recency: component("recency", recencyScore(comparable.capturedAt, sourceCutoffAt)),
    floorAndElevator: component("floorAndElevator", floorAndElevatorScore(subject, comparable)),
    equipment: component("equipment", equipmentScore(subject, comparable))
  };

  const available = Object.values(components).filter((item) => item.available && item.score !== undefined);
  const availableWeight = available.reduce((sum, item) => sum + item.weight, 0);
  const weightedScore = available.reduce((sum, item) => sum + (item.score ?? 0) * item.weight, 0);
  const normalizedScore = availableWeight > 0 ? weightedScore / availableWeight : 0;
  const completeness = availableWeight / 100;
  const completenessPenalty = 0.7 + 0.3 * completeness;
  const finalScore = Math.max(0, Math.min(100, normalizedScore * 100));
  const dataConfidence = calculateDataConfidence(subject, comparable);
  const selectionScore = finalScore * 0.85 + dataConfidence.score * 0.15;
  const valuationWeightScore = Math.max(0, Math.min(100, finalScore * completenessPenalty));

  return {
    methodologyVersion: MARKET_METHODOLOGY_VERSION,
    components,
    availableWeight,
    completeness: Math.round(completeness * 1000) / 1000,
    completenessPenalty: Math.round(completenessPenalty * 1000) / 1000,
    normalizedScore: Math.round(normalizedScore * 1000) / 1000,
    finalScore: Math.round(finalScore * 10) / 10,
    selectionScore: Math.round(selectionScore * 10) / 10,
    valuationWeightScore: Math.round(valuationWeightScore * 10) / 10,
    dataConfidence,
    explanations: comparisonExplanations(subject, comparable, comparable.capturedAt, sourceCutoffAt)
  };
};

export const describeComparableDifferences = (
  subject: MarketSubjectSnapshot,
  comparable: MarketAnalysisComparable
) => {
  const differences: string[] = [];
  const location = locationMatch(subject, comparable);
  if (location === "different-granular") differences.push(`iná uvedená lokalita: ${granularLocation(comparable)}`);
  else if (location === "different-municipality") differences.push(`iná obec: ${comparable.location?.municipality || comparable.city}`);
  const subjectArea = effectiveApartmentArea(subject).value;
  const comparableArea = effectiveApartmentArea(comparable).value;
  if (subjectArea && comparableArea) {
    const difference = Math.round(comparableArea - subjectArea);
    if (Math.abs(difference) > 2) differences.push(`výmera ${difference > 0 ? "+" : ""}${difference} m²`);
  }
  const subjectCondition = canonicalCondition(subject.technical?.condition ?? subject.condition);
  const comparableCondition = canonicalCondition(comparable.technical?.condition ?? comparable.condition);
  if (subjectCondition !== "unknown" && comparableCondition !== "unknown" && subjectCondition !== comparableCondition) {
    differences.push(`stav: ${comparable.condition}`);
  }
  const fields: Array<[string, string, string]> = [
    ["rekonštrukcia", subject.reconstruction, comparable.reconstruction],
    ["elektroinštalácia", subject.electricity, comparable.electricity],
    ["bytové jadro", subject.coreType, comparable.coreType],
    ["kuchyňa", subject.kitchen, comparable.kitchen],
    ["výťah", subject.elevator, comparable.elevator],
    ["stav domu", subject.buildingCondition, comparable.buildingCondition],
    ["typ stavby", subject.buildingType ?? "", comparable.buildingType ?? ""],
    ["orientácia", subject.orientation, comparable.orientation],
    ["balkón / lodžia", subject.balconyType, comparable.balconyType],
    ["pivnica", subject.cellar, comparable.cellar]
  ];
  fields.forEach(([label, subjectValue, comparableValue]) => {
    const score = textScore(subjectValue, comparableValue);
    if (score !== undefined && score !== 1) differences.push(`${label}: ${comparableValue}`);
  });
  if (subject.floor !== undefined && comparable.floor !== undefined && subject.floor !== comparable.floor) {
    differences.push(`poschodie ${comparable.floor}`);
  }
  return differences.slice(0, 5);
};

export const describeComparableUnknowns = (
  subject: MarketSubjectSnapshot,
  comparable: Pick<MarketAnalysisComparable, keyof MarketPropertySnapshot | "capturedAt">
) => calculateDataConfidence(subject, comparable).unknownAttributes;

export const describeComparableSelection = (comparable: MarketAnalysisComparable) =>
  comparable.scoreBreakdown.explanations.filter((explanation) => explanation.startsWith("✓")).slice(0, 4);
